import React, { useEffect, useState } from 'react';
import { Pencil, Plus, Save, Trash2 } from 'lucide-react';
import api from '@/lib/api';

const PRODUCT_ORDER = ['churchconnect', 'easypeazy', 'ai-website-design', 'unitedundergod', 'operate'];
// Operate is a business service: it never sees a faith domain.
const BUSINESS_ONLY = ['operate'];
const FAITH_TLDS = ['church', 'faith', 'bible', 'christian', 'catholic'];
const faithTld = domain => FAITH_TLDS.includes(String(domain || '').trim().toLowerCase().split('.').pop());
const isFaith = r => r.faith === true || faithTld(r.domain);
// A new row starts open to every product and not marked faith. A .church
// (or other faith ending) is faith automatically and drops Operate; anything
// else is the owner's call. Defaulting faith on hid Operate from vidalia.pro.
const blank = () => ({
  domain: '', label: '', products: [...PRODUCT_ORDER],
  yearly_usd: 10, active: true, faith: false, note: '', _open: true,
});
// Rows that came back from the server are saved: shown as a summary, and
// their domain cannot be retyped. Retyping the only box on the page is how
// vidalia.church was replaced by vidalia.pro (2026-10-22).
const fromServer = list => (list || []).map(d => ({ ...d, _saved: true, _open: false }));
const forServer = rows => rows.map(({ _saved, _open, ...r }) => r);

/**
 * Domains we own and offer as web addresses (e.g. name.vidalia.church).
 * Customers of the products ticked here can pick the domain when they publish;
 * the yearly price is added on top of their website plan.
 */
export default function AdminSharedDomains() {
  const [rows, setRows] = useState([]);
  const [names, setNames] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const [builtIn, setBuiltIn] = useState([]);

  useEffect(() => {
    api.getSharedDomains()
      .then(res => {
        setRows(fromServer(res.data.domains));
        setNames(res.data.products || {});
        setBuiltIn(res.data.built_in || []);
      })
      .catch(() => setErr('Could not load the domain list.'));
  }, []);

  const change = fn => { setRows(fn); setDirty(true); setMsg(''); };
  const update = (i, patch) => change(rs => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const remove = i => {
    const r = rows[i];
    if (r._saved && !window.confirm(`Remove ${r.domain}? Customers stop seeing it once you save.`)) return;
    change(rs => rs.filter((_, j) => j !== i));
  };
  const productNames = r => PRODUCT_ORDER
    .filter(p => (r.products || []).includes(p) && !(BUSINESS_ONLY.includes(p) && isFaith(r)))
    .map(p => names[p] || p)
    .join(', ');
  const toggleProduct = (i, p) => {
    const cur = rows[i].products || [];
    update(i, { products: cur.includes(p) ? cur.filter(x => x !== p) : [...cur, p] });
  };

  const save = async () => {
    setBusy(true); setMsg(''); setErr('');
    try {
      const res = await api.saveSharedDomains({ domains: forServer(rows) });
      const saved = res.data.domains || [];
      setRows(fromServer(saved));
      setDirty(false);
      setMsg(saved.length
        ? `Saved ${saved.length} shared domain${saved.length === 1 ? '' : 's'}: ${saved.map(d => d.domain).join(', ')}.`
        : 'Saved. No shared domains are listed now.');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not save. Please check the list and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 space-y-4" data-testid="admin-shared-domains">
      <div>
        <h2 className="text-lg font-semibold text-white">Shared domains</h2>
        <p className="text-sm text-slate-400 mt-1">
          Domains you own and offer as web addresses, like <span className="text-slate-200">grace.vidalia.church</span>.
          The yearly price is added on top of the customer's website plan. Before a domain goes live, add it to
          Cloudflare so its addresses can be set up automatically.
        </p>
      </div>

      {builtIn.length > 0 && (
        <div className="space-y-2" data-testid="built-in-endings">
          <p className="text-xs uppercase tracking-wide text-slate-500">Included free (built in)</p>
          {builtIn.map(b => (
            <div key={b.domain} className="border border-slate-800 rounded-lg p-4 flex flex-wrap items-start justify-between gap-3 bg-slate-950/40">
              <div className="space-y-1">
                <p className="text-white font-semibold">
                  {b.domain}
                  <span className="ml-2 text-xs font-normal text-emerald-400">Always on</span>
                </p>
                <p className="text-sm text-slate-300">{b.label}</p>
                <p className="text-xs text-slate-400">$0 · {b.who}</p>
              </div>
              <span className="text-xs text-slate-500">Built into the system</span>
            </div>
          ))}
          <p className="text-xs uppercase tracking-wide text-slate-500 pt-2">Your shared domains</p>
        </div>
      )}

      {rows.length === 0 && <p className="text-sm text-slate-500">No shared domains yet.</p>}

      {rows.map((r, i) => (r._saved && !r._open ? (
        <div key={r.domain} className="border border-slate-800 rounded-lg p-4 flex flex-wrap items-start justify-between gap-3"
          data-testid={`shared-domain-${i}`}>
          <div className="space-y-1">
            <p className="text-white font-semibold">
              {r.domain}
              <span className={`ml-2 text-xs font-normal ${r.active !== false ? 'text-emerald-400' : 'text-slate-500'}`}>
                {r.active !== false ? 'Available to customers' : 'Hidden from customers'}
              </span>
              {isFaith(r) && <span className="ml-2 text-xs font-normal text-amber-300">Faith</span>}
            </p>
            {r.label && <p className="text-sm text-slate-300">{r.label}</p>}
            <p className="text-xs text-slate-400">${Number(r.yearly_usd || 0)}/year on top of the website · {productNames(r) || 'No products'}</p>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setRows(rs => rs.map((x, j) => (j === i ? { ...x, _open: true } : x)))}
              className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white">
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
            <button type="button" onClick={() => remove(i)}
              className="inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300">
              <Trash2 className="w-3.5 h-3.5" /> Remove
            </button>
          </div>
        </div>
      ) : (
        <div key={r._saved ? r.domain : `new-${i}`} className="border border-slate-800 rounded-lg p-4 space-y-3" data-testid={`shared-domain-${i}`}>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="text-xs text-slate-400">Domain
              {r._saved ? (
                <p className="mt-1 h-10 flex items-center text-sm text-white font-semibold">{r.domain}</p>
              ) : (
                <input value={r.domain} onChange={e => update(i, { domain: e.target.value })} placeholder="vidalia.church"
                  className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-lg h-10 px-3 text-sm text-white" />
              )}
            </label>
            <label className="text-xs text-slate-400">Shown to customers as
              <input value={r.label} onChange={e => update(i, { label: e.target.value })} placeholder="For Vidalia-area churches"
                className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-lg h-10 px-3 text-sm text-white" />
            </label>
            <label className="text-xs text-slate-400">Price per year ($), on top of the website
              <input type="number" min="0" step="1" value={r.yearly_usd} onChange={e => update(i, { yearly_usd: e.target.value })}
                className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-lg h-10 px-3 text-sm text-white" />
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-300">
            <span className="text-xs text-slate-400">Offered to:</span>
            {PRODUCT_ORDER.map(p => {
              const blocked = BUSINESS_ONLY.includes(p) && isFaith(r);
              return (
                <label key={p} className={`inline-flex items-center gap-2 ${blocked ? 'opacity-40' : ''}`}
                  title={blocked ? 'Operate is a business service, so it is not offered faith domains.' : undefined}>
                  <input type="checkbox" disabled={blocked}
                    checked={!blocked && (r.products || []).includes(p)} onChange={() => toggleProduct(i, p)} />
                  {names[p] || p}
                </label>
              );
            })}
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={isFaith(r)} disabled={faithTld(r.domain)}
              onChange={e => update(i, { faith: e.target.checked })} />
            Faith domain (not offered to Operate){faithTld(r.domain) ? ' — always, for this ending' : ''}
          </label>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={r.active !== false} onChange={e => update(i, { active: e.target.checked })} />
              Available to customers now
            </label>
            <button type="button" onClick={() => remove(i)}
              className="inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300">
              <Trash2 className="w-3.5 h-3.5" /> Remove
            </button>
          </div>
        </div>
      )))}

      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={() => change(rs => [...rs, blank()])}
          className="inline-flex items-center gap-2 border border-slate-700 text-slate-200 rounded-lg px-4 h-10 text-sm">
          <Plus className="w-4 h-4" /> Add a domain
        </button>
        <button type="button" onClick={save} disabled={busy}
          className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white rounded-lg px-4 h-10 text-sm font-semibold">
          <Save className="w-4 h-4" /> {busy ? 'Saving…' : 'Save list'}
        </button>
      </div>
      {dirty && !busy && <p className="text-sm text-amber-300">Not saved yet. Click Save list to keep these changes.</p>}
      {msg && <p className="text-sm text-emerald-400">{msg}</p>}
      {err && <p className="text-sm text-red-400">{err}</p>}
    </section>
  );
}
